/**
 * A modal dialog, and the form that usually lives in one.
 *
 * Built on the native `<dialog>` with `showModal()`, which is what makes this
 * worth 60 lines instead of a library: the focus trap, the return of focus to
 * whatever opened it, Escape, the backdrop, and marking the rest of the page
 * inert are all the platform's job and all done correctly. A hand-rolled overlay
 * gets the visuals right and those five things wrong.
 *
 * `ModalForm` is the same save discipline as EditPanel - submit, surface the
 * error, revalidate, close - because a screen where two forms fail differently
 * is a screen nobody trusts. The difference is only where it appears: inline
 * when the edit is part of the page being read, in a dialog when the edit is a
 * detour from a list the operator wants to keep looking at.
 */
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useRevalidator } from 'react-router';
import { ApiError } from '../api.ts';

export function Modal({
  open,
  onClose,
  title,
  caption,
  wide,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  caption?: string | undefined;
  /** For a dialog carrying a whole record rather than one or two fields. */
  wide?: boolean | undefined;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  /*
   * Escape and the backdrop close the dialog without React knowing, so the
   * element's own close event is what keeps the caller's state honest. Without
   * this, reopening the same dialog after an Escape does nothing: `open` is
   * still true, so the effect above sees no change to make.
   */
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const close = () => onClose();
    dialog.addEventListener('close', close);
    return () => dialog.removeEventListener('close', close);
  }, [onClose]);

  return (
    <dialog className={`modal${wide ? ' modal--wide' : ''}`} ref={ref} aria-labelledby="modal-title">
      <div className="modal__head">
        <h2 id="modal-title" className="modal__title">
          {title}
        </h2>
        <button
          className="modal__close"
          type="button"
          onClick={onClose}
          aria-label="閉じる"
        >
          ✕
        </button>
      </div>
      {caption && <p className="modal__caption">{caption}</p>}
      {children}
    </dialog>
  );
}

/**
 * A dialog whose whole purpose is one write.
 *
 * `danger` is not decoration: it marks the actions that take access away, which
 * are the ones an operator should have to notice they are pressing.
 */
export function ModalForm({
  open,
  onClose,
  title,
  caption,
  onSubmit,
  submitLabel = '保存する',
  danger,
  wide,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  caption?: string | undefined;
  /** Performs the write. Throwing surfaces the message inside the dialog. */
  onSubmit: () => Promise<unknown>;
  submitLabel?: string;
  danger?: boolean;
  /** Three columns instead of two, for a dialog carrying a whole record. */
  wide?: boolean;
  children: ReactNode;
}) {
  const revalidator = useRevalidator();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A dialog that reopens still carrying the last attempt's error is a dialog
  // reporting a failure that is no longer happening.
  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit();
      await revalidator.revalidate();
      onClose();
    } catch (err) {
      /*
       * A plain Error is a check the form did itself before sending anything,
       * and its message is the only thing that says which field is wrong -
       * replacing it with a generic failure makes the dialog unusable.
       */
      setError(
        err instanceof ApiError
          ? [err.message, ...err.fields].join(' / ')
          : err instanceof Error && err.message
            ? err.message
            : '保存できませんでした',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={title} caption={caption} wide={wide}>
      <form onSubmit={(event) => void submit(event)}>
        <div className="editpanel__body">{children}</div>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="editpanel__actions">
          <button
            className={danger ? 'btn btn--danger' : 'btn'}
            type="submit"
            disabled={saving}
          >
            {saving ? '保存中…' : submitLabel}
          </button>
          <button className="btn btn--ghost" type="button" onClick={onClose} disabled={saving}>
            キャンセル
          </button>
        </div>
      </form>
    </Modal>
  );
}
